import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import * as signalR from '@microsoft/signalr';
import { ToastNotification, Theme } from '@carbon/react';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import authService from '../services/authService';
import notificationService from '../services/notificationService';
import { API_CONFIG } from '../config/api.config';
import { toastKindForType } from '../utils/notificationUtils';
import '../components/NotificationToast.css';

const NotificationContext = createContext(null);

/**
 * Custom hook to use the Notification Context.
 * @throws {Error} If used outside of NotificationProvider
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Notification Provider
 * Loads notifications over REST, keeps them in sync via a SignalR connection
 * to /hubs/notifications, and exposes actions to the rest of the app.
 */
export const NotificationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, kind: 'info', title: '', subtitle: '' });

  const connectionRef = useRef(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((kind, title, subtitle) => {
    setToast({ show: true, kind, title, subtitle });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(
      () => setToast((prev) => ({ ...prev, show: false })),
      5000
    );
  }, []);

  /**
   * Load the notification list and unread count over REST.
   */
  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, count] = await Promise.all([
        notificationService.getNotifications(false),
        notificationService.getUnreadCount(),
      ]);
      setNotifications(list);
      setUnreadCount(count);
    } catch (err) {
      console.error('Greška pri učitavanju notifikacija:', err);
      setError('Nije moguće učitati obaveštenja.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Mark a single notification as read.
   */
  const markAsRead = useCallback(async (id) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isUnread: false, readAt: new Date().toISOString() } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));

    try {
      const updated = await notificationService.markAsRead(id);
      if (updated) {
        setNotifications((prev) => prev.map((n) => (n.id === id ? updated : n)));
      }
    } catch (err) {
      console.error('Greška pri označavanju notifikacije kao pročitane:', err);
      // Resync to recover from a failed optimistic update
      refetch();
    }
  }, [refetch]);

  /**
   * Mark all notifications as read.
   */
  const markAllAsRead = useCallback(async () => {
    const previous = notifications;
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, isUnread: false, readAt: n.readAt || new Date().toISOString() }))
    );
    setUnreadCount(0);

    try {
      await notificationService.markAllAsRead();
    } catch (err) {
      console.error('Greška pri označavanju svih notifikacija kao pročitanih:', err);
      setNotifications(previous);
      refetch();
    }
  }, [notifications, refetch]);

  /**
   * Delete a notification.
   */
  const deleteNotification = useCallback(async (id) => {
    const target = notifications.find((n) => n.id === id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (target?.isUnread) {
      setUnreadCount((c) => Math.max(0, c - 1));
    }

    try {
      await notificationService.deleteNotification(id);
    } catch (err) {
      console.error('Greška pri brisanju notifikacije:', err);
      refetch();
    }
  }, [notifications, refetch]);

  // Initial load + SignalR lifecycle, driven by auth state.
  useEffect(() => {
    if (!isAuthenticated) {
      // Logged out: tear everything down.
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
        connectionRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;

    // 1) Initial REST load
    refetch();

    // 2) Open the SignalR connection
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${API_CONFIG.baseURL}/hubs/notifications`, {
        accessTokenFactory: () => authService.getAccessToken() || '',
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on('ReceiveNotification', (notification) => {
      if (!notification) return;
      setNotifications((prev) => {
        // Guard against duplicates (e.g. reconnect resync + push)
        if (prev.some((n) => n.id === notification.id)) return prev;
        return [notification, ...prev];
      });
      if (notification.isUnread !== false) {
        setUnreadCount((c) => c + 1);
      }
      showToast(
        toastKindForType(notification.type),
        notification.title || 'Novo obaveštenje',
        notification.body || ''
      );
    });

    // After a reconnect, resync over REST to catch anything missed while offline.
    connection.onreconnected(() => {
      refetch();
    });

    connectionRef.current = connection;

    connection.start().catch((err) => {
      if (!cancelled) {
        console.error('SignalR konekcija nije uspostavljena:', err);
      }
    });

    return () => {
      cancelled = true;
      connection.stop().catch(() => {});
      if (connectionRef.current === connection) {
        connectionRef.current = null;
      }
    };
  }, [isAuthenticated, refetch, showToast]);

  // Clear the toast timer on unmount.
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const value = {
    notifications,
    unreadCount,
    loading,
    error,
    refetch,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {toast.show && (
        <Theme theme={theme}>
          <div className="notification-toast-container">
            <ToastNotification
              kind={toast.kind}
              title={toast.title}
              subtitle={toast.subtitle}
              timeout={0}
              lowContrast
              onClose={() => setToast((prev) => ({ ...prev, show: false }))}
            />
          </div>
        </Theme>
      )}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
